// Property management phase 9 — Reports (owner, 17 Oct 2026).
//
// One screen for every report in lib/pmReports: pick a report, set its few choices, read
// it, export it (CSV) or print it. The monthly owner packet opens as a printable page.
// The figures are the server's; this screen formats them and adds nothing.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { PageHeader, Panel, Select, Input, Button, Notice, Loading, money, fmtDate } from '../../components/secure/pm/PMKit';

const API = '/api/property-management/reports';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const cell = (type, v) => {
    if (v === null || v === undefined || v === '') return '';
    if (type === 'money') return money(v, '');
    if (type === 'pct') return `${Number(v).toFixed(1)}%`;
    if (type === 'num') return Number(v).toLocaleString();
    if (type === 'date') return fmtDate(v);
    return String(v);
};
const csvOf = (rep) => {
    // Quoted, and a cell that starts like a formula (=, +, -, @) opens as text (audit 2026-11).
    const q = (s) => { const t = String(s ?? ''); return `"${(/^[=+\-@\t\r]/.test(t) ? `'${t}` : t).replace(/"/g, '""')}"`; };
    const head = rep.columns.map((c) => q(c.label)).join(',');
    const lines = [...rep.rows, ...(rep.totals ? [rep.totals] : [])].map((r) => rep.columns.map((c) => q(r[c.key] ?? '')).join(','));
    return [head, ...lines].join('\r\n');
};

async function openHtml(path) {
    let token = null;
    try { token = localStorage.getItem('jwtToken'); } catch { /* storage blocked */ }
    const w = window.open('', '_blank');
    const r = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const html = await r.text();
    if (w) { w.document.open(); w.document.write(r.ok ? html : `<p style="font-family:Arial">${html}</p>`); w.document.close(); }
}

export default function PMReportsPage() {
    const api = useApiClient();
    const [cat, setCat] = useState(null);
    const [key, setKey] = useState('pnl_monthly');
    const now = new Date();
    const [p, setP] = useState({ year: now.getFullYear(), book_id: '', property_id: '', through_month: '', months: 24, days: 90 });
    const [rep, setRep] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [packet, setPacket] = useState({ book_id: '', month: `${now.getFullYear()}-${String(now.getMonth() === 0 ? 12 : now.getMonth()).padStart(2, '0')}` });

    useEffect(() => {
        api.get(API).then((c) => {
            setCat(c);
            if (c.books.length) {
                setP((x) => ({ ...x, book_id: x.book_id || c.books[0].book_id }));
                setPacket((x) => ({ ...x, book_id: x.book_id || c.books[0].book_id }));
            }
        }).catch((e) => setError({ message: e.message }));
    }, [api]);
    // Last month by default for the packet (a month's statement is read after it closes).
    useEffect(() => {
        if (now.getMonth() === 0) setPacket((x) => ({ ...x, month: `${now.getFullYear() - 1}-12` }));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const def = useMemo(() => (cat ? cat.reports.find((r) => r.key === key) : null), [cat, key]);
    const run = useCallback(async () => {
        if (!def) return;
        setBusy(true); setError(null);
        const q = new URLSearchParams();
        for (const k of def.params) if (p[k] !== '' && p[k] !== null && p[k] !== undefined) q.set(k, p[k]);
        try { setRep(await api.get(`${API}/run/${key}?${q}`)); } catch (e) { setRep(null); setError({ message: e.message }); } finally { setBusy(false); }
    }, [api, def, key, p]);
    useEffect(() => { if (def) run(); }, [key, def]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!cat) return error ? <Notice {...error} /> : <Loading />;
    const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
    const has = (k) => def && def.params.includes(k);
    const download = () => {
        const blob = new Blob([`﻿${csvOf(rep)}`], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${key}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    const printReport = () => {
        const head = rep.columns.map((c) => `<th style="text-align:${['money', 'num', 'pct'].includes(c.type) ? 'right' : 'left'}">${c.label}</th>`).join('');
        const row = (r, cls = '') => `<tr class="${r._section ? 'sec' : r._total ? 'tot' : cls}">${rep.columns.map((c) => `<td style="text-align:${['money', 'num', 'pct'].includes(c.type) ? 'right' : 'left'}">${cell(c.type, r[c.key]).replace(/</g, '&lt;')}</td>`).join('')}</tr>`;
        const w = window.open('', '_blank');
        if (!w) return;
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${rep.title}</title><style>body{font-family:Arial,sans-serif;font-size:11px;margin:16px;color:#1e293b}
h1{font-size:16px;margin:0}table{width:100%;border-collapse:collapse;margin-top:8px}td,th{border-bottom:1px solid #e2e8f0;padding:4px 5px;white-space:nowrap}th{font-size:10px;text-transform:uppercase;color:#64748b}
.sec td{font-weight:bold;background:#f1f5f9}.tot td{font-weight:bold;border-top:1.5px solid #334155}@page{size:landscape}</style></head><body>
<h1>${rep.title}</h1><div>${rep.subtitle || ''}</div><table><thead><tr>${head}</tr></thead><tbody>${rep.rows.map((r) => row(r)).join('')}${rep.totals ? row({ ...rep.totals, _total: true }) : ''}</tbody></table>
${(rep.notes || []).map((n) => `<p style="color:#64748b">${n}</p>`).join('')}<script>window.onload=()=>window.print()</script></body></html>`);
        w.document.close();
    };

    return (
        <div>
            <PageHeader title="Property Management Reports" subtitle="Owner statements, P&L, budget, occupancy, leases, receivables, deposits, fees, vendors, maintenance and CAM — each exportable and printable." />

            <Panel title="Monthly owner statement" subtitle="Trust cash, income statement (month and year to date), distributions, rent roll, past-due rent and maintenance — one printable packet per owner.">
                {cat.books.length ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 items-end">
                        <Select label="Owner" value={packet.book_id} onChange={(e) => setPacket({ ...packet, book_id: e.target.value })}
                            options={cat.books.map((b) => ({ value: b.book_id, label: b.legal_name }))} />
                        <Input label="Month" type="month" value={packet.month} onChange={(e) => setPacket({ ...packet, month: e.target.value })} />
                        <div className="mb-3"><Button onClick={() => openHtml(`${API}/owner-packet?book_id=${packet.book_id}&month=${packet.month}`)}>Open statement</Button></div>
                    </div>
                ) : <p className="text-sm text-slate-500">No owner books in your portfolio yet.</p>}
            </Panel>

            <div className="grid md:grid-cols-[16rem_1fr] gap-4">
                <div className="space-y-1">
                    {cat.reports.map((r) => (
                        <button key={r.key} onClick={() => { setKey(r.key); setRep(null); }}
                            className={`w-full text-left px-3 py-2 rounded text-sm ${key === r.key ? 'bg-slate-800 text-white' : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}>
                            <div className="font-medium">{r.title}</div>
                            <div className={`text-xs ${key === r.key ? 'text-slate-300' : 'text-slate-400'}`}>{r.description}</div>
                        </button>
                    ))}
                </div>
                <div className="min-w-0">
                    <Panel>
                        <div className="flex flex-wrap gap-x-4 items-end">
                            {has('year') && <Input label="Year" value={p.year} onChange={set('year')} inputMode="numeric" className="w-24" />}
                            {has('book_id') && <Select label="Owner" value={p.book_id} onChange={set('book_id')} className="w-56"
                                options={[...(key === 'pnl_monthly' ? [{ value: '', label: 'All owners' }] : []), ...cat.books.map((b) => ({ value: b.book_id, label: b.legal_name }))]} />}
                            {has('property_id') && <Select label="Property" value={p.property_id} onChange={set('property_id')} className="w-56"
                                options={[{ value: '', label: 'All properties' }, ...cat.properties.map((x) => ({ value: x.pm_property_id, label: x.name }))]} />}
                            {has('through_month') && <Select label="Through" value={p.through_month} onChange={set('through_month')} className="w-40"
                                options={[{ value: '', label: 'Latest month' }, ...MONTHS.map((m, i) => ({ value: i + 1, label: m }))]} />}
                            {has('months') && <Input label="Months ahead" value={p.months} onChange={set('months')} inputMode="numeric" className="w-28" />}
                            {has('days') && <Input label="Last … days" value={p.days} onChange={set('days')} inputMode="numeric" className="w-28" />}
                            <div className="mb-3 flex gap-2">
                                <Button onClick={run} disabled={busy}>{busy ? 'Running…' : 'Run'}</Button>
                                {rep && rep.rows && <Button variant="ghost" onClick={download}>CSV</Button>}
                                {rep && rep.rows && <Button variant="ghost" onClick={printReport}>Print</Button>}
                            </div>
                        </div>
                        {error && <Notice {...error} onDismiss={() => setError(null)} />}
                        {busy && !rep && <Loading />}
                        {rep && (
                            <div>
                                <h3 className="font-semibold text-slate-800">{rep.title}</h3>
                                {rep.subtitle && <p className="text-sm text-slate-500 mb-2">{rep.subtitle}</p>}
                                {rep.flag && <Notice tone="bad" message={(rep.notes || []).find((n) => /DIFFERENCE/.test(n))} />}
                                {rep.rows.length ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead><tr>{rep.columns.map((c) => (
                                                <th key={c.key} className={`py-2 pr-3 text-xs uppercase tracking-wide text-slate-400 font-medium whitespace-nowrap ${['money', 'num', 'pct'].includes(c.type) ? 'text-right' : 'text-left'}`}>{c.label}</th>))}</tr></thead>
                                            <tbody>
                                                {rep.rows.map((r, i) => (
                                                    <tr key={i} className={`border-t border-slate-100 ${r._section ? 'bg-slate-50 font-semibold' : r._total ? 'font-semibold border-t-2 border-slate-300' : ''}`}>
                                                        {rep.columns.map((c) => (
                                                            <td key={c.key} className={`py-1.5 pr-3 whitespace-nowrap ${['money', 'num', 'pct'].includes(c.type) ? 'text-right tabular-nums' : ''} ${c.key === 'result' && r.result === 'Unfavorable' ? 'text-rose-600' : c.key === 'result' && r.result === 'Favorable' ? 'text-emerald-700' : ''}`}>
                                                                {cell(c.type, r[c.key])}
                                                            </td>))}
                                                    </tr>
                                                ))}
                                                {rep.totals && (
                                                    <tr className="border-t-2 border-slate-400 font-semibold">
                                                        {rep.columns.map((c) => <td key={c.key} className={`py-2 pr-3 whitespace-nowrap ${['money', 'num', 'pct'].includes(c.type) ? 'text-right tabular-nums' : ''}`}>{cell(c.type, rep.totals[c.key])}</td>)}
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : <p className="text-sm text-slate-500 py-4">Nothing to report for these choices.</p>}
                                {(rep.notes || []).filter((n) => !rep.flag || !/DIFFERENCE/.test(n)).map((n) => <p key={n} className="text-xs text-slate-500 mt-2">{n}</p>)}
                            </div>
                        )}
                    </Panel>
                </div>
            </div>
        </div>
    );
}
